/* eslint-disable jsx-a11y/click-events-have-key-events,jsx-a11y/no-static-element-interactions */
import PropTypes from 'prop-types';
import { DragDropContext, Droppable, Draggable } from 'react-beautiful-dnd';
import {
  CityPropType,
  CompanyPropType,
  NewBookingDeliveryStepPropType,
} from 'common/prop-types';
import cx from 'classnames';
import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { connect } from 'react-redux';
import I18n from 'i18n-js';
import { Tooltip } from 'grab-ui';
import closeIcon from 'public/icons/close.svg';
import _uniqueId from 'lodash/uniqueId';
import _get from 'lodash/get';
import _pick from 'lodash/pick';
import MapWithDragSearch from 'components/v2/map-with-drag-search';
import AddressField from 'components/v2/address-field/new';
import {
  isPaxSelector,
  selectedCitySelector,
  userCompanySelector,
  enableMSDSelector,
} from 'selectors/app-selector';
import ScribeDuck from 'ducks/scribe-duck';
import DragHandler from 'public/icons/drag-handle-dots.svg';
import reorder from 'helper/reorder';
import usePostSavingPoiCallback from 'components/v2/common/hooks/use-post-saving-poi-callback';
import styles from './styles.less';
import OverlayBody from '../overlay-body';
import { useFetchSearchAddressFunc } from '../common-hook';

const addressFieldsContainerID = 'addressFieldsContainer';

const makeAddressField = deliveryStep => ({
  key: _uniqueId('newaf'),
  poi: _get(deliveryStep, 'poi'),
  deliveryStep,
});

const defaultPinIndex = addressFields =>
  addressFields.length > 0 ? addressFields.length - 1 : 0;

const AddressSelector = ({
  deliverySteps,
  readOnlyDeliveryStep,
  forceRefresh,
  onChange,
  isPickUp,
  footer,
  allowMultipleAddresses,
  maxAddresses = 1,
  addAddressPlaceholder,
  isPax,
  city,
  company,
  onClose,
  extraActionButtons,
  overlay,
  dispatch,
  deliveryID,
  enableMSD,
}) => {
  const version = 'v2.2';

  // the local state of addressFields, might have empty poi
  const [addressFields, setAddressFields] = useState([]);
  const [lastClickedIndex, setLastClickedIndex] = useState(0);
  // isRoundTrip or multiple dropOffs
  const moreThanOneDisplayStep =
    !isPickUp && (addressFields.length > 1 || !!readOnlyDeliveryStep);
  const indexOnMap =
    lastClickedIndex > -1 && moreThanOneDisplayStep
      ? lastClickedIndex + 1
      : undefined;

  // only auto focus when there is no poi
  const autoFocus = useMemo(() => !_get(deliverySteps, [0, 'poi']), []);

  // small screen to handle keyboard (keyboard +- 300px)
  // focusing only small mobile viewport
  const smallScreen = useMemo(
    () => window.innerHeight <= 675 && window.innerWidth <= 767,
    [],
  );

  const initAddressFields = useCallback(
    () => {
      if (deliverySteps && deliverySteps.length > 0) {
        const addressFields = deliverySteps.map(makeAddressField);
        setAddressFields(addressFields);
        setLastClickedIndex(defaultPinIndex(addressFields));
      }
    },
    [deliverySteps, setAddressFields, setLastClickedIndex],
  );

  // init value
  useEffect(() => initAddressFields(), []);

  // init again when route optimization happens
  useEffect(
    () => {
      if (forceRefresh) {
        initAddressFields();
      }
    },
    [initAddressFields, forceRefresh],
  );

  const onIndexClick = useCallback(
    index => e => {
      if (e && e.target) {
        if (e.target.closest('.ant-select-dropdown')) {
          // don't set index when user select in dropdown
          return;
        }
        setLastClickedIndex(index);
      }
    },
    [setLastClickedIndex],
  );

  const fetchAddressFunc = useFetchSearchAddressFunc({ isPickUp });

  const applyChange = useCallback(
    (addressFields, forceRefresh = false) => {
      let needForceRefresh = forceRefresh || false;
      const newDeliverySteps = addressFields
        .filter(af => !!af.poi)
        .map(({ poi, deliveryStep = {} }) => {
          deliveryStep.poi = _pick(poi, [
            'id',
            'address',
            'latlng',
            'metadata',
          ]);

          const deliveryDetails = _get(poi, 'sp_ext_info');
          // only applicable to newly selected saved poi
          if (deliveryDetails) {
            needForceRefresh = true;

            // always overwrite address note (floor & unit number)
            deliveryStep.locationNote = deliveryDetails.address_details;

            // If saved poi has either name OR phone#: Overwrite both name & phone#
            const name = deliveryDetails.contact_name;
            const phone = deliveryDetails.contact_number;
            if (name || phone) {
              deliveryStep.name = name;
              deliveryStep.phone = phone;
            }
          }
          return deliveryStep;
        });
      onChange(newDeliverySteps, needForceRefresh);
    },
    [onChange],
  );

  const onAddressChangeAt = useCallback(
    index => (poi, poiMeta) => {
      addressFields[index].poi = poi;
      setAddressFields([...addressFields]);
      applyChange(addressFields);
      if (poi && poiMeta) {
        dispatch(
          ScribeDuck.creators.scribeExpressPOISelectPOIFromResult({
            ...poiMeta,
            version,
            selectedPOI: _get(poi, 'id'),
            poiType: isPickUp ? 'PICKUP' : 'DROPOFF',
          }),
        );
      }
    },
    [addressFields, setAddressFields, applyChange, dispatch, isPickUp, version],
  );

  const onAddNewStop = useCallback(
    (poi, poiMeta) => {
      if (poi) {
        const newAddressFields = [...addressFields, makeAddressField({ poi })];
        setAddressFields(newAddressFields);
        applyChange(newAddressFields);
        setLastClickedIndex(newAddressFields.length - 1);
        if (!isPickUp) {
          dispatch(ScribeDuck.creators.scribeExpressPOISearchWebAddStop());
        }
        if (poiMeta) {
          dispatch(
            ScribeDuck.creators.scribeExpressPOISelectPOIFromResult({
              ...poiMeta,
              version,
              selectedPOI: _get(poi, 'id'),
              poiType: isPickUp ? 'PICKUP' : 'DROPOFF',
            }),
          );
        }
      }
    },
    [
      addressFields,
      setAddressFields,
      setLastClickedIndex,
      applyChange,
      dispatch,
      isPickUp,
      version,
    ],
  );

  const onAddressDeleteAt = useCallback(
    index => evt => {
      // prevent trigger `onIndexClick`
      evt.stopPropagation();
      const newAddressFields = [...addressFields];
      newAddressFields.splice(index, 1);
      setAddressFields(newAddressFields);
      applyChange(newAddressFields, true);

      if (
        lastClickedIndex === -1 ||
        index === lastClickedIndex ||
        lastClickedIndex >= newAddressFields.length
      ) {
        setLastClickedIndex(defaultPinIndex(newAddressFields));
      }
    },
    [
      addressFields,
      setAddressFields,
      applyChange,
      lastClickedIndex,
      setLastClickedIndex,
    ],
  );

  const onDragEnd = useCallback(
    result => {
      const sourceIndex = _get(result, 'source.index');
      const destIndex = _get(result, 'destination.index');
      if (sourceIndex === undefined || destIndex === undefined) {
        return;
      }
      const newAddressFields = reorder(addressFields, sourceIndex, destIndex);
      setAddressFields(newAddressFields);

      // Clear route optimization
      applyChange(newAddressFields, true);
    },
    [setAddressFields, addressFields, applyChange],
  );

  const onMapSearchResult = useCallback(
    (poi, searchParams) => {
      if (lastClickedIndex >= 0) {
        onAddressChangeAt(lastClickedIndex)(poi);
      } else {
        onAddNewStop(poi, undefined);
      }
      if (searchParams) {
        const { latitude, longitude, pickupOrDropoff } = searchParams;
        dispatch(
          ScribeDuck.creators.scribeExpressPOISearchMapSearchResult({
            latitude,
            longitude,
            poiType: pickupOrDropoff,
            poiID: _get(poi, 'id'),
          }),
        );
      }
    },
    [onAddressChangeAt, onAddNewStop, lastClickedIndex, dispatch],
  );

  const postSavingPoiCallback = usePostSavingPoiCallback();

  const renderAddressField = useCallback(
    (addressField, index, addressFields) => (
      <Draggable
        key={addressField.key}
        draggableId={addressField.key}
        index={index}
      >
        {innerProvided => (
          <div {...innerProvided.draggableProps} ref={innerProvided.innerRef}>
            <div
              key={addressField.key}
              className={styles.addressField}
              onClick={onIndexClick(index)}
            >
              <Tooltip
                placement="topLeft"
                title={I18n.t('v2.locationDrawer.tooltip.dragHandle')}
              >
                <img
                  className={cx(styles.dragHandle, {
                    [styles.hidden]: addressFields.length <= 1,
                  })}
                  alt="dragHandle"
                  {...innerProvided.dragHandleProps}
                  src={DragHandler}
                />
              </Tooltip>
              <div className={styles.addressFieldContainer}>
                <AddressField
                  city={city}
                  onChange={onAddressChangeAt(index)}
                  poi={addressField.poi}
                  isPickUp={isPickUp}
                  showNumbers={moreThanOneDisplayStep}
                  number={index + 1}
                  fetchAddressFunc={fetchAddressFunc}
                  allowDelete={addressFields.length > 1}
                  onDelete={onAddressDeleteAt(index)}
                  placeholder={addAddressPlaceholder}
                  autoFocus={!smallScreen && index === 0 && autoFocus}
                  searchInOverlay={smallScreen}
                  postSavingPoiCallback={postSavingPoiCallback(
                    deliveryID,
                    index,
                  )}
                />
              </div>
            </div>
          </div>
        )}
      </Draggable>
    ),
    [
      autoFocus,
      city,
      onIndexClick,
      isPickUp,
      fetchAddressFunc,
      onAddressChangeAt,
      onAddressDeleteAt,
      addAddressPlaceholder,
      moreThanOneDisplayStep,
    ],
  );

  const extraEmptyStopDom = useMemo(
    () => {
      // Don't show additional drop off location field when enableMSD is false and it's not pickup
      if (!enableMSD) {
        return undefined;
      }
      if (!allowMultipleAddresses || !_get(addressFields, [0, 'poi'])) {
        return undefined;
      }
      const tooManyAF = addressFields.length >= maxAddresses;
      return (
        <div
          className={styles.addressField}
          onClick={tooManyAF ? undefined : onIndexClick(-1)}
        >
          <div className={cx(styles.addressFieldContainer, styles.empty)}>
            <AddressField
              city={city}
              onChange={onAddNewStop}
              isPickUp={isPickUp}
              showNumbers={false}
              fetchAddressFunc={fetchAddressFunc}
              allowDelete={false}
              disabled={tooManyAF}
              placeholder={
                tooManyAF
                  ? I18n.t('v2.anytimeBooking.cantAddMoreStops')
                  : addAddressPlaceholder
              }
              isPlaceholder
              searchInOverlay={smallScreen}
              deliveryID={deliveryID}
            />
          </div>
        </div>
      );
    },
    [
      enableMSD,
      allowMultipleAddresses,
      maxAddresses,
      addAddressPlaceholder,
      addressFields,
      city,
      onAddNewStop,
      fetchAddressFunc,
      onIndexClick,
      isPickUp,
    ],
  );

  const readOnlyDeliveryStepDom = useMemo(
    () => {
      if (!readOnlyDeliveryStep) {
        return undefined;
      }
      return (
        <div className={styles.addressField}>
          <div className={cx(styles.addressFieldContainer, styles.readOnly)}>
            <AddressField
              disabled
              poi={readOnlyDeliveryStep.poi}
              isPickUp={isPickUp}
              showNumbers={false}
              allowDelete={false}
              isRoundTrip
              deliveryID={deliveryID}
            />
          </div>
        </div>
      );
    },
    [readOnlyDeliveryStep, isPickUp],
  );

  // render address fields
  const renderedAddressFields = useMemo(
    () => (
      <DragDropContext onDragEnd={onDragEnd}>
        <Droppable droppableId={'droppable'}>
          {provided => (
            <div
              {...provided.droppableProps}
              id={addressFieldsContainerID}
              ref={provided.innerRef}
            >
              {addressFields.map(renderAddressField)}
              {provided.placeholder}
            </div>
          )}
        </Droppable>
      </DragDropContext>
    ),
    [addressFields, onDragEnd, renderAddressField],
  );

  const onConfirm = useCallback(
    () => {
      dispatch(
        ScribeDuck.creators.scribeExpressPOISearchWebConfirm(
          deliveryID,
          isPickUp,
        ),
      );
      onClose();
    },
    [applyChange, addressFields, onClose, deliveryID, isPickUp],
  );

  const displayMapLatLng = useMemo(
    () => {
      if (lastClickedIndex >= 0 && lastClickedIndex < addressFields.length) {
        const { poi } = addressFields[lastClickedIndex];
        if (poi) {
          return poi.latlng;
        }
      }
      return undefined;
    },
    [addressFields, lastClickedIndex],
  );

  return (
    <OverlayBody showConfirm onConfirmClick={onConfirm} footer={footer}>
      <a className={styles.closeButton} onClick={onClose}>
        <img src={closeIcon} alt="close" />
      </a>
      <MapWithDragSearch
        key={'drag-map'}
        city={city}
        latlng={displayMapLatLng}
        displayPinIndex={indexOnMap}
        showPinIndex={!isPickUp && indexOnMap !== undefined}
        isSender={isPickUp}
        onMapSearchResult={onMapSearchResult}
        className={cx({ [styles.bigMap]: smallScreen })}
      />
      <div
        className={cx(styles.addressFields, {
          [styles.smallAddressFields]: smallScreen,
        })}
      >
        {renderedAddressFields}
        {extraEmptyStopDom}
        {readOnlyDeliveryStepDom}
        <div className={styles.actions}>{extraActionButtons}</div>
      </div>
      {overlay}
    </OverlayBody>
  );
};

AddressSelector.propTypes = {
  city: CityPropType,
  company: CompanyPropType,
  deliverySteps: PropTypes.arrayOf(NewBookingDeliveryStepPropType),
  readOnlyDeliveryStep: NewBookingDeliveryStepPropType,
  forceRefresh: PropTypes.bool,
  footer: PropTypes.node,
  extraActionButtons: PropTypes.arrayOf(PropTypes.node),
  overlay: PropTypes.node,
  isPax: PropTypes.bool.isRequired,
  isPickUp: PropTypes.bool.isRequired,
  onChange: PropTypes.func.isRequired,
  allowMultipleAddresses: PropTypes.bool,
  maxAddresses: PropTypes.number,
  addAddressPlaceholder: PropTypes.string,
  onClose: PropTypes.func.isRequired,
  dispatch: PropTypes.func.isRequired,
  deliveryID: PropTypes.string.isRequired,
  enableMSD: PropTypes.bool.isRequired,
};

AddressSelector.defaultProps = {
  city: {},
  forceRefresh: false,
  company: undefined,
  deliverySteps: [],
  readOnlyDeliveryStep: undefined,
  footer: undefined,
  extraActionButtons: undefined,
  overlay: undefined,
  allowMultipleAddresses: false,
  maxAddresses: 1,
  addAddressPlaceholder: '',
};

export default connect((state, props) => ({
  isPax: isPaxSelector(state),
  company: userCompanySelector(state),
  city: selectedCitySelector(state),
  enableMSD: enableMSDSelector(state),
}))(AddressSelector);
